import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import useCurrency from '../hooks/useCurrency';

const CurrencyContext = createContext();

export const useCurrencyContext = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrencyContext must be used within a CurrencyProvider');
    }
    return context;
};

export const CurrencyProvider = ({ children, defaultCurrency = 'USD' }) => {
    const { currentUser } = useAuth();

    // Initialize from localStorage or default
    const [currency, setCurrencyState] = useState(() => {
        const saved = localStorage.getItem('nexora_client_currency');
        return saved || defaultCurrency;
    });

    // Use hook with base currency 'USD' (assuming app prices are in USD)
    const { rates, loading, error, convert: hookConvert, formatPrice: hookFormatPrice } = useCurrency('usd');

    // Sync with user profile on login
    useEffect(() => {
        const syncUserProfile = async () => {
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        if (userData.currency && userData.currency !== currency) {
                            setCurrencyState(userData.currency);
                            localStorage.setItem('nexora_client_currency', userData.currency);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user currency preference:", error);
                }
            }
        };

        syncUserProfile();
    }, [currentUser]);

    const setCurrency = async (newCurrency) => {
        setCurrencyState(newCurrency);
        localStorage.setItem('nexora_client_currency', newCurrency);

        // Update user profile if logged in
        if (currentUser) {
            try {
                const userRef = doc(db, 'users', currentUser.uid);
                await updateDoc(userRef, { currency: newCurrency });
            } catch (error) {
                console.error("Error updating currency preference:", error);
            }
        }
    };

    /**
     * Wrapper for convert that automatically uses the current selected currency
     * if no target currency is provided.
     * Assumes input amount is in USD.
     */
    const convertPrice = (amount, targetCurrency = currency) => {
        return hookConvert(amount, targetCurrency);
    };

    /**
     * Wrapper for formatPrice that automatically uses the current selected currency.
     * Assumes input amount is in USD.
     */
    const formatAppPrice = (amount, targetCurrency = currency) => {
        return hookFormatPrice(amount, targetCurrency);
    };

    // Common currencies list for dropdowns
    const availableCurrencies = [
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },
        { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
        { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
        { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
        { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee' },
        { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
        { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
        { code: 'AED', symbol: 'dh', name: 'UAE Dirham' },
        { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    ];

    const value = {
        currency,
        setCurrency,
        rates,
        loading,
        error,
        convertPrice,
        formatPrice: formatAppPrice,
        availableCurrencies
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

export default CurrencyContext;
